/**
 * Auth Service for Asertis API
 * Handles JWT token management with auto-refresh on expiry.
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_LOGIN_URL = process.env.REACT_APP_API_LOGIN_URL;
const API_LOGIN_USER = process.env.REACT_APP_API_LOGIN_USER;
const API_LOGIN_PASS = process.env.REACT_APP_API_LOGIN_PASS;
const APP_CORS = process.env.REACT_APP_CORS || "";
const IS_DEV_MODE = process.env.REACT_APP_DEV_MODE === "true";
const CORS_TARGET_URL = "https://hhkqnrfciyctnsz2oo2cgzgb6q.apigateway.sa-bogota-1.oci.customer-oci.com/asertis-360";

let accessToken = null;
let tokenExpiry = null;

const stripCorsProxy = (url) => {
    if (!url) {
        return url;
    }

    const normalizedUrl = String(url);
    return APP_CORS && normalizedUrl.startsWith(APP_CORS)
        ? normalizedUrl.slice(APP_CORS.length)
        : normalizedUrl;
};

const buildApiUrl = (url, options = {}) => {
    if (!url) {
        return url;
    }

    const normalizedUrl = stripCorsProxy(url);

    if (!APP_CORS || !/^https?:\/\//i.test(normalizedUrl)) {
        return normalizedUrl;
    }

    if (!shouldUseCorsProxy(normalizedUrl, options)) {
        return normalizedUrl;
    }

    return `${APP_CORS}${normalizedUrl}`;
};

const shouldUseCorsProxy = (url, options = {}) => {
    if (!IS_DEV_MODE || typeof window === "undefined") {
        return false;
    }

    const hostname = window.location.hostname;
    const isLocalHost = hostname === "localhost" || hostname === "127.0.0.1";

    if (!isLocalHost) {
        return false;
    }

    try {
        const parsedUrl = new URL(url);
        const allowedTargets = Array.isArray(options.allowedOrigins)
            ? options.allowedOrigins.filter(Boolean)
            : [API_BASE_URL, CORS_TARGET_URL];

        return allowedTargets.some((target) => {
            try {
                const parsedTarget = new URL(target);
                const normalizedTargetHref = parsedTarget.href.replace(/\/+$/, "");
                const normalizedUrlHref = parsedUrl.href.replace(/\/+$/, "");

                return (
                    parsedTarget.origin === parsedUrl.origin ||
                    normalizedUrlHref.startsWith(normalizedTargetHref)
                );
            } catch (error) {
                return false;
            }
        });
    } catch (error) {
        return false;
    }
};

/**
 * Login to Asertis API and store token in memory.
 */
const login = async () => {
    try {
        const resolvedLoginUrl = API_LOGIN_URL || `${API_BASE_URL}/login`;
        const loginUrl = buildApiUrl(resolvedLoginUrl);

        const response = await fetch(loginUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
            body: JSON.stringify({
                login: API_LOGIN_USER,
                password: API_LOGIN_PASS,
            }),
        });

        if (!response.ok) {
            throw new Error(`Login failed: ${response.status}`);
        }

        const data = await response.json();
        accessToken = data.access_token;
        // Refresh 60 seconds before actual expiry to avoid edge cases
        tokenExpiry = Date.now() + (data.expires_in - 60) * 1000;
        console.log("Asertis API: Token obtenido correctamente");
        return accessToken;
    } catch (error) {
        console.error("Error al iniciar sesión en Asertis API:", error);
        throw error;
    }
};

/**
 * Get current token, refreshing if expired or about to expire.
 */
const getValidToken = async () => {
    if (!accessToken || Date.now() >= tokenExpiry) {
        await login();
    }
    return accessToken;
};

/**
 * Wrapper around fetch that injects Bearer token and auto-refreshes on 401.
 */
const authFetch = async (url, options = {}) => {
    let token = await getValidToken();

    const makeRequest = async (authToken) => {
        const headers = {
            Accept: "application/json",
            ...options.headers,
            Authorization: `Bearer ${authToken}`,
        };

        if (options.body && !headers["Content-Type"]) {
            headers["Content-Type"] = "application/json";
        }

        return fetch(buildApiUrl(url), { ...options, headers });
    };

    let response = await makeRequest(token);

    // If 401 Unauthorized, refresh token and retry once
    if (response.status === 401) {
        console.log("Asertis API: Token expirado, renovando...");
        accessToken = null;
        tokenExpiry = null;
        token = await getValidToken();
        response = await makeRequest(token);
    }

    return response;
};

/**
 * Clear stored token (for logout or reset).
 */
const clearToken = () => {
    accessToken = null;
    tokenExpiry = null;
};

export { login, getValidToken, authFetch, clearToken, buildApiUrl, shouldUseCorsProxy };
